import type { PropertyDefinition } from '../gql/PropertyDefinition'
import React, { useLayoutEffect, useRef, useState } from 'react'
import {
  Box,
  IconButton,
  InputBase,
  MenuItem,
  Select,
  type SxProps,
  Typography,
} from '@mui/material'
import LockIcon from '@mui/icons-material/Lock'
import { isObjectPropertyType, objectPropertyTypes, type PropertyType } from '../gql/PropertyType'
import './CreateObjectTypePage.css'
import clsx from 'clsx'
import CloseIcon from '@mui/icons-material/Close'
import type { Theme } from '@mui/material/styles'

export const propertyChipConfig = {
  height: 40,
  fontSize: 20,
}

export const propertyChipConfigCompact = {
  height: 32,
  fontSize: 15,
}

export interface PropertyDefinitionChipProps {
  initialValues: PropertyDefinition
  editable: boolean
  typeEditable?: boolean
  compact?: boolean
  nameColor: string
  typeColor: string
  onCloseClicked?: () => void
  onNameChange?: (name: string) => void
  onTypeChange?: (type: PropertyType) => void
}

export function PropertyDefinitionChip({
  initialValues,
  editable,
  typeEditable,
  compact,
  nameColor,
  typeColor,
  onCloseClicked,
  onNameChange,
  onTypeChange,
}: PropertyDefinitionChipProps) {
  const isTypeEditable = typeEditable ?? editable
  const [propName, setPropName] = useState(initialValues.name)
  const [propType, setPropType] = useState(initialValues.type)
  const [isHoveredOn, setIsHoveredOn] = useState(false)
  const mirrorRef = useRef<HTMLSpanElement>(null)
  const [mirrorWidth, setMirrorWidth] = useState(0)

  const cfg = compact ? propertyChipConfigCompact : propertyChipConfig
  const chipHeight = `${cfg.height}px`
  const iconSize = compact ? 20 : 25
  const hoverExpansion = editable ? iconSize * 1.2 : 0
  const inputWidth = mirrorWidth
    ? `${mirrorWidth + (isHoveredOn ? hoverExpansion : 0)}px`
    : `${propName.length + 1}ch`

  useLayoutEffect(() => {
    if (mirrorRef.current) setMirrorWidth(mirrorRef.current.offsetWidth)
  }, [propName])

  const handleNameChange = (newName: string) => {
    setPropName(newName)
    onNameChange?.(newName)
  }

  const handleTypeChange = (newType: PropertyType) => {
    setPropType(newType)
    onTypeChange?.(newType)
  }

  const fontSize = cfg.fontSize

  const halfMixin: Record<string, any> = {
    px: 0.5,
    height: 'auto',
  }
  if (editable) {
    halfMixin['&:hover'] = {
      borderColor: (theme: Theme) => theme.palette.secondary.main,
    }
  }

  return (
    <>
      <Box
        height={chipHeight}
        sx={{
          position: 'relative',
          display: 'flex',
          alignItems: 'center',
          marginRight: isHoveredOn ? `-${hoverExpansion}px` : 0,
          zIndex: isHoveredOn ? 1 : 0,
          bgcolor: 'background.paper',
          transition: 'padding-right 0.12s ease, margin-right 0.12s ease',
          '&:hover .closeBtn': {
            opacity: 1,
            pointerEvents: 'auto',
          },
        }}
        onPointerEnter={() => setIsHoveredOn(true)}
        onPointerLeave={() => setIsHoveredOn(false)}
      >
        <Box
          className={clsx('propDefChipHalf', 'propDefChipLeftHalf')}
          sx={{ ...halfMixin, color: typeColor, justifyContent: 'flex-end' }}
          minWidth={compact ? 30 : 60}
        >
          {isTypeEditable ? (
            <Select
              disableUnderline
              variant="standard"
              name="propType"
              value={propType}
              onChange={(e) => {
                const val = e.target.value
                if (isObjectPropertyType(val)) handleTypeChange(val)
              }}
              size="small"
              sx={{
                color: 'inherit',
                '& .MuiSelect-select': { padding: '2px 4px' },
                fontSize,
              }}
            >
              {objectPropertyTypes.map((t) => (
                <MenuItem key={t} value={t}>
                  {t}
                </MenuItem>
              ))}
            </Select>
          ) : (
            <Typography fontSize={fontSize} variant="body2">
              {propType}
            </Typography>
          )}
        </Box>

        <Box
          className={clsx('propDefChipHalf', 'propDefChipRightHalf')}
          sx={{ ...halfMixin, color: nameColor, justifyContent: 'flex-start' }}
        >
          {editable ? (
            <>
              <span
                ref={mirrorRef}
                aria-hidden
                style={{
                  position: 'absolute',
                  visibility: 'hidden',
                  whiteSpace: 'pre',
                  fontSize,
                  padding: '2px 4px',
                  pointerEvents: 'none',
                }}
              >
                {propName || ' '}
              </span>
              <InputBase
                name="propName"
                value={propName}
                onChange={(e) => handleNameChange(e.target.value)}
                sx={{
                  color: 'inherit',
                  fontSize,
                  minWidth: 30,
                  maxWidth: 500,
                  width: inputWidth,
                  transition: 'width 0.12s ease',
                  '& input': { padding: '2px 4px' },
                }}
              />
            </>
          ) : (
            <>
              <LockIcon sx={{ fontSize }} />
              <Typography fontSize={fontSize} variant="body2">
                {propName}
              </Typography>
            </>
          )}
        </Box>

        {editable && (
          <IconButton
            size="small"
            className="closeBtn"
            onClick={onCloseClicked}
            sx={{
              position: 'absolute',
              top: '50%',
              right: `${(cfg.height - iconSize) / 2}px`,
              transform: 'translateY(-50%)',
              width: iconSize,
              height: iconSize,
              borderRadius: '50%',
              backgroundColor: 'background.paper',
              boxShadow: 1,
              opacity: 0,
              transition: 'opacity 0.15s',
              color: (theme) => theme.palette.error.main,
              border: (theme) => `1px solid ${theme.palette.error.main}`,
              '&:hover': {
                border: (theme) => `2px solid ${theme.palette.error.dark}`,
              },
              '& svg': {
                fontSize: 14,
                stroke: (theme) => theme.palette.error.main,
                strokeWidth: 1,
              },
              '&:hover svg': {
                stroke: (theme) => theme.palette.error.dark,
                strokeWidth: 2,
              },
              '&:focus-visible': {
                boxShadow: (theme) => `0 0 0 3px ${theme.palette.error.light}`,
              },
              '&:active': {
                boxShadow: 'none',
              },
            }}
          >
            <CloseIcon />
          </IconButton>
        )}
      </Box>
    </>
  )
}
